// Opportunities Module Tour
// Visual sales pipeline and deal tracking

export const opportunitiesTour = {
  id: 'opportunities',
  name: 'Opportunities Module Tour',
  displayName: 'Master Your Sales Pipeline',
  description: 'Visualize deals, track progress, and close more sales',
  estimatedDuration: '5 minutes',
  category: 'feature',
  autoStart: false,
  prerequisiteTours: ['ftux'],
  
  config: {
    animate: true,
    overlayOpacity: 0.75,
    smoothScroll: true,
    allowClose: true,
    showProgress: true
  },
  
  steps: [
    // Step 1: Welcome to Opportunities
    {
      element: 'nav #sb_opportunities',
      popover: {
        title: '🎯 Welcome to Opportunities',
        description: `
          This is your sales pipeline—where leads become paying clients. Track every 
          deal through each stage of your sales process. See what's stuck, what's 
          moving, and what's about to close. Your pipeline has never been clearer!
        `,
        side: 'right',
        align: 'center'
      }
    },

    // Step 2: What is an Opportunity?
    {
      popover: {
        title: '💼 What\'s an Opportunity?',
        description: `
          An opportunity (or "deal") represents a potential sale. Each opportunity has:
          • **Contact**: Who you're selling to
          • **Value**: How much the deal is worth
          • **Stage**: Where they are in your sales process
          • **Status**: Open, Won, Lost, or Abandoned
          
          Think of it as a sales record that moves through your pipeline until it closes.
        `,
        side: 'over',
        align: 'center'
      }
    },

    // Step 3: Pipeline Structure
    {
      popover: {
        title: '🚰 Understanding Pipelines',
        description: `
          A pipeline is your sales process broken into stages. For example:
          
          **Lead** → **Qualified** → **Proposal** → **Negotiation** → **Closed Won**
          
          Each stage represents a step closer to winning the deal. Opportunities 
          move from left to right as they progress. When a deal closes, it leaves 
          the pipeline (marked Won or Lost).
        `,
        side: 'over',
        align: 'center',
        proTip: 'Different products/services may need different pipelines. Create multiple pipelines for different sales processes!'
      }
    },

    // Step 4: Create New Pipeline
    {
      element: 'button:contains("Create New Pipeline"), button:contains("New Pipeline")',
      popover: {
        title: '➕ Create Your First Pipeline',
        description: `
          Start by creating a pipeline for your sales process:
          1. Click "Create New Pipeline"
          2. Name it (e.g., "Sales Pipeline", "Consulting Deals")
          3. Define your stages (Lead, Qualified, Proposal, etc.)
          4. Set probability for each stage (how likely to close)
          
          You can have multiple pipelines for different products or services.
        `,
        side: 'left',
        align: 'start'
      }
    },

    // Step 5: Pipeline View (Kanban Board)
    {
      element: '[class*="pipeline"], [class*="kanban"], [class*="board"]',
      popover: {
        title: '📊 Kanban Board View',
        description: `
          Your pipeline displays as a kanban board—each column is a stage. 
          Opportunities appear as cards. You can:
          • **Drag cards** between stages to update progress
          • **Click a card** to see full details
          • **See value totals** at the top of each column
          • **Filter** by team member, date, or value
          
          This visual approach makes it easy to spot bottlenecks at a glance.
        `,
        side: 'top',
        align: 'center'
      }
    },

    // Step 6: Create New Opportunity
    {
      element: 'button:contains("Add Opportunity"), button:contains("New Opportunity")',
      popover: {
        title: '💰 Add a New Opportunity',
        description: `
          When you have a potential sale, create an opportunity:
          1. Click "Add Opportunity"
          2. Select the contact (or create a new one)
          3. Enter the deal value
          4. Choose the starting stage
          5. Add notes or tasks
          
          The opportunity appears in your pipeline immediately.
        `,
        side: 'left',
        align: 'start',
        proTip: 'Link opportunities to form submissions so new leads automatically create opportunities!'
      }
    },

    // Step 7: Opportunity Card Details
    {
      element: '[class*="opportunity-card"], [class*="deal-card"]',
      popover: {
        title: '🎴 Opportunity Card',
        description: `
          Each card shows key info at a glance:
          • Contact name
          • Deal value ($5,000)
          • Days in current stage
          • Last activity date
          • Assigned team member
          
          Click the card to see full details, add notes, set tasks, or update the value.
        `,
        side: 'left',
        align: 'center'
      }
    },

    // Step 8: Drag and Drop
    {
      popover: {
        title: '🖱️ Drag to Update Progress',
        description: `
          Moving deals through your pipeline is as simple as drag and drop:
          • Grab an opportunity card
          • Drag it to the next stage
          • Drop it
          
          The stage updates automatically, triggering any workflows you've set up 
          for that stage (like "Send proposal email when moved to Proposal stage").
        `,
        side: 'over',
        align: 'center'
      }
    },

    // Step 9: Opportunity Details Panel
    {
      popover: {
        title: '📝 Full Opportunity Details',
        description: `
          Click any opportunity to open the details panel where you can:
          • **Edit value** and contact info
          • **Add notes** about conversations or next steps
          • **Create tasks** for follow-up actions
          • **View activity** timeline
          • **Attach files** like proposals or contracts
          • **Mark as Won or Lost** when it closes
        `,
        side: 'over',
        align: 'center'
      }
    },

    // Step 10: Pipeline Stages Management
    {
      popover: {
        title: '⚙️ Customize Pipeline Stages',
        description: `
          Tailor stages to match your sales process:
          • **Add stages**: Click the "+" at the end
          • **Rename stages**: Edit the column headers
          • **Reorder stages**: Drag columns left or right
          • **Set probabilities**: 25% chance at Qualified, 75% at Negotiation, etc.
          
          Probabilities help forecast revenue based on what's in your pipeline.
        `,
        side: 'over',
        align: 'center'
      }
    },

    // Step 11: Filters and Views
    {
      element: '[class*="filter"], select, [role="combobox"]',
      popover: {
        title: '🔍 Filter Your Pipeline',
        description: `
          Narrow down what you see:
          • **By team member**: See only your deals (or your team's)
          • **By date range**: This month, this quarter, custom
          • **By value**: Deals over $10K
          • **By stage**: Focus on one part of the process
          
          Filters help you focus on what matters right now.
        `,
        side: 'left',
        align: 'center'
      }
    },

    // Step 12: Pipeline Reporting
    {
      popover: {
        title: '📈 Pipeline Analytics',
        description: `
          Track your sales performance with built-in reports:
          • **Conversion rates**: % of leads that become customers
          • **Average deal size**: Typical value per opportunity
          • **Sales velocity**: How fast deals move through pipeline
          • **Win/loss analysis**: Why deals succeed or fail
          • **Forecasting**: Predict future revenue based on probabilities
          
          Go to Reporting > Pipeline for detailed analytics.
        `,
        side: 'over',
        align: 'center'
      }
    },

    // Step 13: Automation with Pipelines
    {
      popover: {
        title: '⚡ Automate Based on Pipeline Movement',
        description: `
          Workflows can trigger when opportunities change:
          • **Stage changed**: Send proposal when moved to "Proposal" stage
          • **Opportunity won**: Create invoice, send thank you email
          • **Stuck too long**: Notify sales manager if no movement in 7 days
          • **Value changed**: Alert when deal value increases
          
          Combine pipelines with automation for a hands-free sales machine!
        `,
        side: 'over',
        align: 'center',
        proTip: 'Set up a workflow to automatically mark opportunities as "Lost" if they haven\'t moved in 90 days!'
      }
    },

    // Step 14: Completion
    {
      popover: {
        title: '🎉 You\'re a Pipeline Pro!',
        description: `
          Excellent work! You now know how to:
          ✅ Create pipelines for your sales process
          ✅ Add and manage opportunities
          ✅ Drag deals through stages
          ✅ Track values and probabilities
          ✅ Filter and analyze your pipeline
          ✅ Automate actions based on pipeline changes
          
          **Next Steps:**
          • Create your first pipeline with your actual sales stages
          • Add a few opportunities (practice with test data)
          • Set up workflow to auto-create opportunities from forms
          • Check out Reporting tour for deeper pipeline analytics
          
          Watch your close rate soar! 📈
        `,
        side: 'over',
        align: 'center',
        showButtons: ['close'],
        doneBtnText: 'Close More Deals! →'
      }
    }
  ]
};
